// Ported from dev-scrips-pm-react services/scheduling/use-retrieve-slots.ts (PROD-1190 calendar port)
import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { AppointmentSlot } from '@/types/scheduling-api';

/**
 * POST /api/Appointment/RetrieveSlots
 *
 * Mirrors Flutter `retrieveSlots()` →
 * `scheduling_data_source_impl.dart:980-997`. Used by the Recurrence
 * configurator's "Check availability" button. Returns per-occurrence
 * slot details + an `allSlotsAvailable` flag.
 *
 * Body shape mirrors `RecurrenceProperties.toRetrieveSlotsJson()`
 * (recurrence_properties_model.dart:82-95) — `days` only when period is "Weeks".
 */

export interface RetrieveSlotsParams {
  providerId: string;
  practiceId: string;
  appointmentProfileId: string;
  startDate: string;
  endDate: string;
  repeatEvery: number;
  numberOfRepeat: number;
  /** Backend period enum — "Days" | "Weeks" | "Months". */
  period: string;
  /** Full weekday names ("Monday", "Tuesday", …). */
  days?: string[];
}

export interface SlotDetail {
  appointmentTime?: string;
  /** Confirmed slot for this occurrence (already on the doctor's calendar). */
  slot?: AppointmentSlot;
  /** Backend-suggested replacement slot — used when the original time is unavailable. */
  selectedSlot?: AppointmentSlot;
  /** Backend ships exactly "Available" | "Not available" | "Skipped"
   * (app_constants.dart:802-804 — note the space + lowercase 'a'). Always
   * normalise via `effectiveSlotStatus` in recurrence-conflict.ts; never
   * compare this raw field against a string literal at a call site. */
  slotStatus?: string;
  isSkipped?: boolean;
  nearestSlots?: AppointmentSlot[];
  /**
   * Provider chosen via the per-occurrence "Select another doctor" re-selection
   * (PROD-1011). Local-only — the backend never ships this; it's set when the
   * user reassigns one occurrence to a different doctor. The picked
   * `selectedSlot` already carries the new `providerId` (that's what books);
   * this is kept for the "Doctor has been changed." note + display.
   */
  selectedDoctor?: { id: string; name: string };
  /**
   * Explicit doctor-changed flag set by a user re-selection (PROD-1011).
   * Distinct from the backend-derived case (a suggested slot under a different
   * provider with empty `nearestSlots`) — see `isDoctorChanged` in
   * recurrence-conflict.ts, which honours BOTH.
   */
  isDoctorChanged?: boolean;
}

export interface SlotsDataModel {
  allSlotsAvailable?: boolean;
  slotDetails?: SlotDetail[];
}

function buildBody(params: RetrieveSlotsParams) {
  return {
    providerId: params.providerId,
    practiceId: params.practiceId,
    appointmentProfileId: params.appointmentProfileId,
    startDate: params.startDate,
    endDate: params.endDate,
    repeatEvery: params.repeatEvery,
    numberOfRepeat: params.numberOfRepeat,
    period: params.period,
    days: params.period === 'Weeks' ? (params.days ?? []) : null,
  };
}

export function useRetrieveSlots() {
  return useMutation<SlotsDataModel, Error, RetrieveSlotsParams>({
    mutationFn: (params) =>
      post<SlotsDataModel>('/api/Appointment/RetrieveSlots', buildBody(params)),
  });
}
